import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  adminFetchBtwStatuses,
  adminCreateBtwStatus,
  adminUpdateBtwStatus,
  adminArchiveBtwStatus,
  adminReorderBtwStatuses,
} from "../../core/axios/endpoints";
import { COLOR_OPTIONS, getStatusColor } from "../../core/btw-status-colors";
import StatusLozenge, { CATEGORY_OPTIONS, categoryOf } from "../../components/status-lozenge";
import ConfirmModal from "../../components/confirm-modal";

const ColorSwatch = ({ color, selected, onClick }) => {
  const c = getStatusColor(color);
  return (
    <button
      type="button"
      onClick={onClick}
      title={color}
      className={`flex h-7 w-7 items-center justify-center rounded-full transition ${
        selected ? "ring-2 ring-brand ring-offset-2" : "hover:scale-110"
      }`}
    >
      <span className={`h-4 w-4 rounded-full ${c.dot}`} />
    </button>
  );
};

const SUBSIDIE_LABEL = {
  "In-behandeling": "In behandeling",
  "Ingediend-bij-rvo": "Ingediend bij RVO",
  Goedgekeurd: "Goedgekeurd",
  Afgekeurd: "Afgekeurd",
};

// Statuscategorie (lozenge-kleur op het bord) en de koppeling met de
// subsidie-status, zodat subsidie-aanvragen in dezelfde kolom vallen.
const CategoryAndSubsidieFields = ({ draft, setDraft, showSubsidie = true }) => (
  <>
    <label className="block text-sm">
      <span className="text-xs font-semibold text-ink-subtlest">Categorie</span>
      <select
        value={draft.category}
        onChange={(e) => setDraft({ ...draft, category: e.target.value })}
        className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
      >
        {CATEGORY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-ink-subtlest">Bepaalt de kleur van het label op het bord (grijs, blauw, groen) en of de kolom als Klaar telt.</span>
    </label>
    {showSubsidie ? (
    <label className="block text-sm">
      <span className="text-xs font-semibold text-ink-subtlest">RVO-status in deze kolom</span>
      <select
        value={draft.subsidieStatus}
        onChange={(e) => setDraft({ ...draft, subsidieStatus: e.target.value })}
        className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
      >
        <option value="">Geen</option>
        {Object.entries(SUBSIDIE_LABEL).map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-ink-subtlest">Sleep je een aanvraag naar deze kolom, dan wordt de echte RVO-status gewijzigd (met bevestiging, factuur en mail). Elke RVO-status hoort bij één kolom per type.</span>
    </label>
    ) : null}
  </>
);

const MODULES = [
  { key: "btwBatterij", label: "BTW Batterij", board: "/panel/admin/borden/alle?type=btw" },
  { key: "eia", label: "EIA", board: "/panel/admin/borden/alle?type=eia" },
  { key: "eiaZon", label: "EIA Zon", board: "/panel/admin/borden/alle?type=eiaZon" },
  { key: "isde", label: "ISDE", board: "/panel/admin/borden/alle?type=isde" },
];

const StatusRow = ({ status, isEditing, onEdit, onCancel, onSave, onArchive, busy }) => {
  const [draft, setDraft] = useState({
    name: status.name,
    color: status.color,
    isTerminal: status.isTerminal,
    isDefault: status.isDefault,
    category: categoryOf(status),
    subsidieStatus: status.subsidieStatus ?? "",
  });

  useEffect(() => {
    setDraft({
      name: status.name,
      color: status.color,
      isTerminal: status.isTerminal,
      isDefault: status.isDefault,
      category: categoryOf(status),
      subsidieStatus: status.subsidieStatus ?? "",
    });
  }, [status.name, status.color, status.isTerminal, status.isDefault, status.category, status.subsidieStatus, isEditing]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status._id, disabled: isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const c = getStatusColor(status.color);
  const draftC = getStatusColor(draft.color);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded bg-white p-4 shadow-card transition ${
        isEditing ? "ring-2 ring-brand" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className={`flex h-8 w-6 cursor-grab items-center justify-center text-ink-subtlest hover:text-ink active:cursor-grabbing ${
            isEditing ? "cursor-not-allowed opacity-30" : ""
          }`}
          aria-label="Versleep"
          disabled={isEditing}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </button>

        <span className={`h-3 w-3 flex-shrink-0 rounded-full ${c.dot}`} />

        <div className="min-w-0 flex-1">
          {isEditing ? (
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              autoFocus
              className="w-full rounded border-2 border-line bg-white px-2 py-1 text-sm font-medium text-ink focus:border-brand focus:outline-none"
            />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ink">{status.name}</span>
              {status.isDefault ? (
                <span className="inline-flex items-center rounded bg-[#DCFFF1] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#1F845A]">
                  Standaard
                </span>
              ) : null}
              {status.isTerminal ? (
                <span className="inline-flex items-center rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                  Eindstation
                </span>
              ) : null}
              <StatusLozenge status={status} text={CATEGORY_OPTIONS.find((o) => o.value === categoryOf(status))?.label} size="sm" />
              {status.subsidieStatus ? (
                <span className="inline-flex items-center rounded bg-[#F3F0FF] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#5E4DB2]" title="Subsidie-aanvragen met deze status vallen in deze kolom op het bord">
                  RVO: {SUBSIDIE_LABEL[status.subsidieStatus] ?? status.subsidieStatus}
                </span>
              ) : null}
              {status.archived ? (
                <span className="inline-flex items-center rounded bg-[#FFECEB] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#AE2A19]">
                  Gearchiveerd
                </span>
              ) : null}
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => onSave(draft)}
              disabled={busy || !draft.name.trim()}
              className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-ink-subtle hover:bg-[#091E420F]"
            >
              Bewerken
            </button>
            <button
              type="button"
              onClick={onArchive}
              className={`inline-flex h-8 items-center rounded px-3 text-sm font-medium hover:bg-[#091E420F] ${
                status.archived ? "text-brand" : "text-ink-subtlest"
              }`}
            >
              {status.archived ? "Herstellen" : "Archiveren"}
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="mt-4 grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
              Kleur
            </div>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((co) => (
                <ColorSwatch
                  key={co}
                  color={co}
                  selected={draft.color === co}
                  onClick={() => setDraft({ ...draft, color: co })}
                />
              ))}
            </div>
            <div className="mt-3">
              <span className="text-xs text-ink-subtlest">Voorbeeld: </span>
              <span
                className={`ml-1 inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${draftC.pill}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${draftC.dot}`} />
                {draft.name || status.name}
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={draft.isTerminal}
                onChange={(e) => setDraft({ ...draft, isTerminal: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-line text-brand focus:ring-0"
              />
              <span className="text-sm">
                <span className="font-medium text-ink">Eindstation</span>
                <span className="block text-xs text-ink-subtlest">
                  Aanvragen in deze status worden niet meegerekend als &lsquo;vastgelopen&rsquo;.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={draft.isDefault}
                onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-line text-brand focus:ring-0"
              />
              <span className="text-sm">
                <span className="font-medium text-ink">Standaard-status</span>
                <span className="block text-xs text-ink-subtlest">
                  Nieuwe aanvragen starten in deze status. Slechts één status kan standaard zijn.
                </span>
              </span>
            </label>
            <CategoryAndSubsidieFields draft={draft} setDraft={setDraft} showSubsidie={(status.module ?? "btwBatterij") !== "btwBatterij"} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const NewStatusForm = ({ onCancel, onSave, busy, module = "btwBatterij" }) => {
  const [draft, setDraft] = useState({
    name: "",
    color: "slate",
    isTerminal: false,
    isDefault: false,
    category: "in_progress",
    subsidieStatus: "",
  });
  const c = getStatusColor(draft.color);
  return (
    <div className="rounded bg-white p-4 shadow-card ring-2 ring-brand">
      <div className="mb-3 flex items-center gap-2">
        <span className={`h-3 w-3 rounded-full ${c.dot}`} />
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Naam van de nieuwe status"
          autoFocus
          className="flex-1 rounded border-2 border-line bg-white px-2 py-1 text-sm text-ink focus:border-brand focus:outline-none"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
            Kleur
          </div>
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((co) => (
              <ColorSwatch
                key={co}
                color={co}
                selected={draft.color === co}
                onClick={() => setDraft({ ...draft, color: co })}
              />
            ))}
          </div>
        </div>
        <div className="space-y-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isTerminal}
              onChange={(e) => setDraft({ ...draft, isTerminal: e.target.checked })}
              className="h-4 w-4 rounded border-line text-brand focus:ring-0"
            />
            Eindstation
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isDefault}
              onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
              className="h-4 w-4 rounded border-line text-brand focus:ring-0"
            />
            Standaard-status
          </label>
          <CategoryAndSubsidieFields draft={draft} setDraft={setDraft} showSubsidie={module !== "btwBatterij"} />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2 border-t border-line pt-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={() => onSave(draft)}
          disabled={busy || !draft.name.trim()}
          className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? "Aanmaken…" : "Status aanmaken"}
        </button>
      </div>
    </div>
  );
};

const BtwBatterijStatussenPage = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [archivePrompt, setArchivePrompt] = useState(null);
  // Statussen per type: elk type (BTW Batterij, EIA, EIA Zon, ISDE) heeft
  // zijn eigen kolommen op het bord.
  const [module, setModule] = useState(() => new URLSearchParams(window.location.search).get("module") || "btwBatterij");
  const moduleInfo = MODULES.find((m) => m.key === module) ?? MODULES[0];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = (withArchived) => {
    setLoading(true);
    return adminFetchBtwStatuses({ includeArchived: withArchived ?? showArchived, module })
      .then((res) => setStatuses(res?.data ?? res ?? []))
      .catch((err) =>
        setError(err?.response?.data?.error ?? err.message ?? "Onbekende fout"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setEditingId(null);
    setCreating(false);
    load(showArchived);
  }, [showArchived, module]);

  const activeIds = useMemo(
    () => statuses.filter((s) => !s.archived).map((s) => s._id),
    [statuses],
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = activeIds.indexOf(active.id);
    const newIdx = activeIds.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(activeIds, oldIdx, newIdx);
    const items = reordered.map((id, order) => ({ id, order }));

    setStatuses((prev) => {
      const map = Object.fromEntries(prev.map((s) => [s._id, s]));
      const archivedTail = prev.filter((s) => s.archived);
      return [...reordered.map((id) => ({ ...map[id], order: items.find((it) => it.id === id).order })), ...archivedTail];
    });

    try {
      await adminReorderBtwStatuses(items);
    } catch (err) {
      console.error(err);
      await load();
    }
  };

  const handleSave = async (id, draft) => {
    setBusy(true);
    try {
      await adminUpdateBtwStatus(id, draft);
      await load();
      setEditingId(null);
    } catch (err) {
      window.alert(err?.response?.data?.error ?? "Bijwerken mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (draft) => {
    setBusy(true);
    try {
      await adminCreateBtwStatus({ ...draft, module });
      await load();
      setCreating(false);
    } catch (err) {
      window.alert(err?.response?.data?.error ?? "Aanmaken mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!archivePrompt) return;
    setBusy(true);
    try {
      await adminArchiveBtwStatus(archivePrompt._id, {
        archive: !archivePrompt.archived,
      });
      await load();
      setArchivePrompt(null);
    } catch (err) {
      window.alert(err?.response?.data?.error ?? "Archiveren mislukt.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-wrapper px-8 pt-20 pb-12">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
            <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
            <span className="mx-1">/</span>
            <Link to="/panel/admin/btw-batterij-board" className="hover:underline">{moduleInfo.label}</Link>
            <span className="mx-1">/</span>
            <span>Statussen</span>
          </nav>
          <h1 className="text-2xl font-medium text-ink">
            Workflow-statussen
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-subtlest">
            Beheer de statussen die aanvragen kunnen doorlopen. Sleep om de volgorde
            op het board te wijzigen. Gearchiveerde statussen blijven gekoppeld aan
            bestaande aanvragen maar verschijnen niet meer in keuze-menu&apos;s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={moduleInfo.board}
            className="inline-flex h-8 items-center gap-1 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M12.7 15.7a1 1 0 01-1.4 0l-5-5a1 1 0 010-1.4l5-5a1 1 0 111.4 1.4L8.4 10l4.3 4.3a1 1 0 010 1.4z"
                clipRule="evenodd"
              />
            </svg>
            Naar bord {moduleInfo.label}
          </Link>
          <label className="flex h-8 cursor-pointer items-center gap-2 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-line text-brand focus:ring-0"
            />
            Toon gearchiveerd
          </label>
          <button
            type="button"
            onClick={() => setCreating(true)}
            disabled={creating}
            className="inline-flex h-8 items-center gap-1.5 rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Nieuwe status
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
          {error}
        </div>
      ) : null}

      <div className="mb-4 flex items-center gap-1 border-b border-line">
        {MODULES.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setModule(m.key)}
            className={`-mb-px px-3 py-2 text-sm font-medium transition ${
              module === m.key ? "border-b-2 border-brand text-brand" : "border-b-2 border-transparent text-ink-subtle hover:text-brand"
            }`}
          >
            {m.label}
          </button>
        ))}
        <span className="ml-auto pb-2 text-xs text-ink-subtlest">Elk type heeft eigen kolommen op het bord.</span>
      </div>

      <div className="space-y-4">
        {creating ? (
          <NewStatusForm
            busy={busy}
            module={module}
            onCancel={() => setCreating(false)}
            onSave={handleCreate}
          />
        ) : null}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={activeIds} strategy={verticalListSortingStrategy}>
            {statuses
              .filter((s) => !s.archived)
              .map((s) => (
                <StatusRow
                  key={s._id}
                  status={s}
                  isEditing={editingId === s._id}
                  busy={busy}
                  onEdit={() => setEditingId(s._id)}
                  onCancel={() => setEditingId(null)}
                  onSave={(draft) => handleSave(s._id, draft)}
                  onArchive={() => setArchivePrompt(s)}
                />
              ))}
          </SortableContext>
        </DndContext>

        {showArchived && statuses.some((s) => s.archived) ? (
          <div className="pt-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
              Gearchiveerd
            </div>
            <div className="space-y-4 opacity-80">
              {statuses
                .filter((s) => s.archived)
                .map((s) => {
                  const c = getStatusColor(s.color);
                  return (
                    <div
                      key={s._id}
                      className="flex items-center gap-3 rounded bg-white p-4 shadow-card"
                    >
                      <span className={`h-3 w-3 rounded-full ${c.dot}`} />
                      <span className="flex-1 text-sm text-ink-subtle">{s.name}</span>
                      <button
                        type="button"
                        onClick={() => setArchivePrompt(s)}
                        className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-brand hover:bg-[#091E420F]"
                      >
                        Herstellen
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : null}

        {!loading && statuses.length === 0 ? (
          <div className="rounded border border-dashed border-line px-4 py-12 text-center text-sm text-ink-subtlest">
            Geen statussen geconfigureerd.
          </div>
        ) : null}
      </div>

      <ConfirmModal
        isOpen={!!archivePrompt}
        title={
          archivePrompt?.archived
            ? "Status herstellen?"
            : "Status archiveren?"
        }
        message={
          archivePrompt?.archived
            ? "Deze status wordt weer beschikbaar in keuze-menu's."
            : "Aanvragen op deze status moeten worden verplaatst voordat je 'm kunt archiveren. De server zal je waarschuwen als er nog aanvragen gekoppeld zijn."
        }
        confirmLabel={archivePrompt?.archived ? "Herstellen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={busy}
        onConfirm={handleArchive}
        onCancel={() => setArchivePrompt(null)}
      />

    </div>
  );
};

export default BtwBatterijStatussenPage;
